// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { createAuthPlugin, disableAuthGate } from './composables/useAuth';

// 🔵 Intent: Task 006 - Bootstrap Icons CSS for the polished admin UI. The
// Bootstrap 5 stylesheet itself is imported inside App.vue's <style> block.
import 'bootstrap-icons/font/bootstrap-icons.css';

const app = createApp(App);
app.use(createPinia());

// Auth0 configuration is injected by cfn_bucket_loader at deploy time via
// api_endpoints.js (window.AUTH0_DOMAIN / AUTH0_CLIENT_ID / AUTH0_AUDIENCE),
// with VUE_APP_* build-time env vars as the local-dev fallback. When the
// domain is empty, disable the login gate; only a development build can then
// call the BFF (see useApiClient.js), so this is a local-dev affordance.
//
// SYSTEM_ADMIN_ORGANIZATION is the Auth0 Organization that gates access to
// this SPA: only members can sign in. Auth0 enforces membership at
// /authorize before issuing a token, so the BFF does not need its own role
// check. The name is fixed across environments since dev / stg use
// separate Auth0 tenants - no collision.
const SYSTEM_ADMIN_ORGANIZATION = 'vwr-system-admins';
const authDomain = window.AUTH0_DOMAIN || process.env.VUE_APP_AUTH0_DOMAIN || '';
const authClientId = window.AUTH0_CLIENT_ID || process.env.VUE_APP_AUTH0_CLIENT_ID || '';
const authAudience = window.AUTH0_AUDIENCE || process.env.VUE_APP_AUTH0_AUDIENCE || undefined;
if (authDomain) {
  app.use(createAuthPlugin({
    authority: `https://${authDomain}`,
    clientId: authClientId,
    redirectUri: `${window.location.origin}/callback`,
    audience: authAudience,
    organization: SYSTEM_ADMIN_ORGANIZATION,
    scope: 'openid profile email',
  }));
} else {
  disableAuthGate();
}

app.mount('#app');
